<template>
  <div class="customer-info-step pa-4">
    <!-- 顧客紐付けセクション (卓全体に対して任意で複数追加) -->
    <v-card
      flat
      outlined
      class="mb-4"
    >
      <v-card-subtitle class="pa-3 pb-1 text-base font-weight-bold">
        顧客の紐付け（任意）
      </v-card-subtitle>
      <v-card-text class="pt-1">
        <div
          class="d-flex flex-wrap align-center"
          style="gap: 8px;"
        >
          <v-chip
            v-for="id in selectedCustomerIds"
            :key="`customer-chip-${id}`"
            close
            color="primary"
            outlined
            @click:close="removeCustomerId(id)"
          >
            {{ getCustomerName(id) }}
          </v-chip>

          <v-btn
            outlined
            small
            color="primary"
            @click="customerDialogVisible = true"
          >
            <v-icon
              left
              small
            >
              {{ icons.mdiPlus }}
            </v-icon>
            顧客を選択
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- 指名 / リファラル サブタブ
         既存 TableCreateDrawer と同条件: cast(users) が登録されており、
         かつ各々の vendible マスタが登録されている場合のみ表示する。 -->
    <template v-if="showNominationTab || showReferralTab">
      <v-tabs
        v-model="activeTab"
        grow
        class="mb-3"
      >
        <v-tab v-if="showNominationTab">
          指名
        </v-tab>
        <v-tab v-if="showReferralTab">
          リファラル
        </v-tab>
      </v-tabs>

      <v-tabs-items :value="activeTab">
        <!-- 指名 -->
        <v-tab-item v-if="showNominationTab">
          <div class="pa-2">
            <nomination-radio-row
              v-for="cast in normalizedUsers"
              :key="`nrr-${cast.id}`"
              :cast="cast"
              :nomination-defs="nominationDefs"
              :selected-vendible-id="getNominationVendibleIdFor(cast.id)"
              @change="onNominationChange(cast, $event)"
            />
          </div>
        </v-tab-item>

        <!-- リファラル -->
        <v-tab-item v-if="showReferralTab">
          <div class="pa-2">
            <div
              v-for="(row, idx) in referralRows"
              :key="`ref-row-${idx}`"
              class="d-flex align-center mb-2"
              style="gap: 8px;"
            >
              <v-select
                :value="row.referralId"
                :items="referralOptions"
                item-text="name"
                item-value="id"
                label="リファラル"
                dense
                outlined
                hide-details
                @input="updateReferralRow(idx, { referralId: $event })"
              />
              <v-select
                :value="row.userId"
                :items="userOptions"
                item-text="name"
                item-value="id"
                label="紹介者"
                dense
                outlined
                hide-details
                @input="updateReferralRow(idx, { userId: $event })"
              />
              <v-btn
                icon
                small
                @click="removeReferralRow(idx)"
              >
                <v-icon>{{ icons.mdiClose }}</v-icon>
              </v-btn>
            </div>
            <v-btn
              text
              small
              color="primary"
              class="mt-2"
              @click="addReferralRow"
            >
              <v-icon left>
                {{ icons.mdiPlus }}
              </v-icon>
              リファラルを追加
            </v-btn>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </template>

    <div
      v-else
      class="text-center pa-4 text--secondary"
    >
      指名・リファラル設定は不要です（キャストまたはマスタが未登録）
    </div>

    <!-- 顧客選択ダイアログ -->
    <v-dialog
      v-model="customerDialogVisible"
      max-width="500"
    >
      <v-card>
        <v-card-title>顧客を検索</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="customerSearch"
            label="名前で検索"
            outlined
            dense
            hide-details
            autofocus
          />
          <v-list
            two-line
            class="mt-3 customer-result-list"
          >
            <v-list-item
              v-for="c in filteredCustomers"
              :key="c.id"
              @click="addCustomerId(c.id)"
            >
              <v-list-item-content>
                <v-list-item-title class="d-flex align-center" style="gap: 8px;">
                  <span class="font-weight-medium">{{ c.name }}</span>
                  <v-chip
                    v-if="c.memberNumber"
                    x-small
                    outlined
                    color="primary"
                  >
                    No.{{ c.memberNumber }}
                  </v-chip>
                  <v-chip
                    v-for="tag in (c.customerTags || []).slice(0, 2)"
                    :key="`tag-${tag.id || tag.name}`"
                    x-small
                    label
                    color="grey lighten-3"
                  >
                    {{ tag.name || tag }}
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle class="d-flex align-center text-caption" style="gap: 12px;">
                  <span v-if="c.visitCount != null">来店 {{ c.visitCount }}回</span>
                  <span v-if="c.lastVisitedAt">前回 {{ formatDate(c.lastVisitedAt) }}</span>
                  <span v-if="c.birthday">誕生日 {{ formatBirthday(c.birthday) }}</span>
                  <span
                    v-if="c.casts && c.casts.length > 0"
                    class="text--secondary"
                  >
                    担当: {{ c.casts.slice(0, 2).map(x => x.name || x).join(', ') }}{{ c.casts.length > 2 ? '…' : '' }}
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="filteredCustomers.length === 0 && !isSearching"
              disabled
            >
              <v-list-item-content class="text--secondary">
                {{ customerSearch ? '該当する顧客が見つかりません' : '名前を入力してください' }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="isSearching"
              disabled
            >
              <v-list-item-content class="text--secondary">
                検索中…
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="customerDialogVisible = false"
          >
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  ref, computed, watch, inject,
} from '@vue/composition-api'
import { mdiPlus, mdiClose } from '@mdi/js'
import CustomerApi from '@/api/v2/admin/customer-management/Customer'
import NominationRadioRow from '../parts/NominationRadioRow.vue'

// data:[{id, attributes:{name,...}}] と {id, name,...} の両方を受け取れるように
const flatten = item => {
  if (!item) return null
  if (item.attributes) return { id: item.id, ...item.attributes }

  return item
}

export default {
  name: 'CustomerInfoStep',
  components: { NominationRadioRow },
  props: {
    nominations: { type: Array, default: () => [] },
    users: { type: Array, default: () => [] },
    referrals: { type: Array, default: () => [] },
    selectedCustomerIds: { type: Array, default: () => [] },
    castNominations: { type: Array, default: () => [] }, // [{ castId, vendibleId, sellingPrice }]
    referralRowsValue: { type: Array, default: () => [] }, // [{ referralId, userId }]
  },
  emits: [
    'update:selectedCustomerIds',
    'update:castNominations',
    'update:referralRowsValue',
  ],
  setup(props, { emit }) {
    const activeTab = ref(0)
    const customerDialogVisible = ref(false)
    const customerSearch = ref('')

    // 既存 OrderCard.vendibleRecipientUsers と同じロジック:
    // - role=cast のキャストのみ
    // - 出勤中（usersWithTimecard）がいればそちらを優先、いなければ全キャスト
    const usersWithTimecard = inject('usersWithTimecard', ref([]))

    const normalizedAllCasts = computed(() => props.users
      .map(flatten)
      .filter(u => u && u.role === 'cast'))

    const normalizedWorkingCasts = computed(() => (usersWithTimecard.value || [])
      .map(flatten)
      .filter(u => u && u.role === 'cast'))

    const normalizedUsers = computed(() => (
      normalizedWorkingCasts.value.length > 0
        ? normalizedWorkingCasts.value
        : normalizedAllCasts.value
    ))

    // 各 Nomination Vendible が1つの「種別」を表す（本指名/場内指名/同伴 等）
    const nominationDefs = computed(() => props.nominations
      .map(flatten)
      .filter(Boolean)
      .map(n => ({
        id: n.id,
        name: n.name || '指名',
        sellingPrice: n.sellingPrice || 0,
      })))

    const referralOptions = computed(() => props.referrals
      .map(flatten)
      .filter(Boolean)
      .map(r => ({ id: r.id, name: r.name || `referral#${r.id}` })))

    const userOptions = computed(() => normalizedUsers.value
      .map(u => ({ id: u.id, name: u.name || `user#${u.id}` })))

    // 既存 TableCreateDrawer.vue と同条件でタブ表示可否を判定
    const showNominationTab = computed(() => (
      nominationDefs.value.length > 0 && normalizedUsers.value.length > 0
    ))
    const showReferralTab = computed(() => (
      referralOptions.value.length > 0 && normalizedUsers.value.length > 0
    ))

    // 検索結果と「選択済み顧客の名前キャッシュ」を統合管理
    const searchResults = ref([])
    const knownCustomers = ref({}) // { [id]: { id, name } }
    const isSearching = ref(false)

    const fetchCustomers = async query => {
      isSearching.value = true
      try {
        const res = await CustomerApi.getCustomers({ q: (query || '').trim() })
        const list = (res?.data?.data || []).map(flatten).filter(Boolean)
        searchResults.value = list
        // キャッシュにマージ
        list.forEach(c => {
          knownCustomers.value = { ...knownCustomers.value, [c.id]: c }
        })
      } catch (_) {
        searchResults.value = []
      } finally {
        isSearching.value = false
      }
    }

    // ダイアログを開いた / 検索クエリ変更時に検索
    watch(customerDialogVisible, v => {
      if (v) fetchCustomers(customerSearch.value)
    })
    watch(customerSearch, q => {
      if (customerDialogVisible.value) fetchCustomers(q)
    })

    const filteredCustomers = computed(() => searchResults.value.slice(0, 50))

    const getCustomerName = id => {
      const c = knownCustomers.value[id]

      return c?.name || `顧客#${id}`
    }

    const addCustomerId = id => {
      if (props.selectedCustomerIds.includes(id)) return
      emit('update:selectedCustomerIds', [...props.selectedCustomerIds, id])
      customerDialogVisible.value = false
      customerSearch.value = ''
    }
    const removeCustomerId = id => {
      emit('update:selectedCustomerIds', props.selectedCustomerIds.filter(x => x !== id))
    }

    // 指名: cast毎に1つの Nomination Vendible を選ぶ
    const getNominationVendibleIdFor = castId => {
      const found = (props.castNominations || []).find(n => n.castId === castId)

      return found?.vendibleId || null
    }
    const onNominationChange = (cast, { vendibleId, def }) => {
      const others = (props.castNominations || []).filter(n => n.castId !== cast.id)
      const next = vendibleId
        ? [...others, { castId: cast.id, vendibleId, sellingPrice: def?.sellingPrice || 0 }]
        : others
      emit('update:castNominations', next)
    }

    // リファラル
    const referralRows = computed(() => props.referralRowsValue || [])
    const addReferralRow = () => {
      emit('update:referralRowsValue', [...referralRows.value, { referralId: null, userId: null }])
    }
    const updateReferralRow = (idx, patch) => {
      emit('update:referralRowsValue', referralRows.value.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
    }
    const removeReferralRow = idx => {
      emit('update:referralRowsValue', referralRows.value.filter((_, i) => i !== idx))
    }

    const formatDate = iso => {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')

      return `${y}/${m}/${day}`
    }

    const formatBirthday = iso => {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')

      return `${m}/${day}`
    }

    return {
      activeTab,
      customerDialogVisible,
      customerSearch,
      isSearching,
      formatDate,
      formatBirthday,
      normalizedUsers,
      nominationDefs,
      referralOptions,
      userOptions,
      showNominationTab,
      showReferralTab,
      filteredCustomers,
      getCustomerName,
      addCustomerId,
      removeCustomerId,
      getNominationVendibleIdFor,
      onNominationChange,
      referralRows,
      addReferralRow,
      updateReferralRow,
      removeReferralRow,
      icons: { mdiPlus, mdiClose },
    }
  },
}
</script>
